import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import moment from 'moment';
import { Line, Bar } from 'react-chartjs-2';
import {
    FormGroup, Input, Table, Alert, Button,
} from 'reactstrap';

import {
    fetchExternalApiStats, presetRange, PERIOD_PRESETS, INTERVAL_OPTIONS,
    MAX_RANGE_DAYS, authErrorLabel, authMethodLabel,
} from '../helpers/externalApiStats';
import { formatNumber, formatPercent, formatMs } from '../helpers/format';
import { formatPoints, formatLoadPercent, costModelLines, loadBadgeClass, loadBarColor } from '../helpers/externalApiCost';
import ClientAutocompleteInput from './clientAutocompleteInput';
import ExternalApiInsights from './externalApiInsights';
import ExternalApiReplicas from './externalApiReplicas';
import { OTHER_COLOR, CLIENT_COLORS, clientDisplayName } from '../helpers/externalApiOverview';
import { isKnownReplica } from '../helpers/externalApiReplicas';

/**
* External API analytics (VD-725): requests, rates/peaks, a timeseries chart
* and a set of breakdown tables. Used both as the standalone "External API
* Analytics" page and as the "API analytics" tab on the client detail page
* (pass `clientId` to scope everything to that one client).
*
* Color: the timeseries uses the documented status palette (errors = critical,
* auth failures = warning, total requests = muted context ink) since those
* series mean good/bad, not identity. The auth-method breakdown is identity
* (which method), so it uses the first three validated categorical slots in
* their fixed, documented order - both combinations are used exactly as
* published in the dataviz skill's reference palette, nothing re-derived.
*/

const palette = {
    surface: '#FFFFFF',
    border: '#E6EDF5',
    borderStrong: '#D2D6DA',
    textPrimary: '#3A3F56',
    textSecondary: '#52514E',
    textMuted: '#818FA5',
    // categorical slots 1-3 (fixed order, validated all-pairs both modes)
    catApiKey: '#2a78d6',
    catBasic: '#eb6834',
    catNone: '#1baf7a',
    // status palette (fixed, reserved meaning)
    good: '#0ca30c',
    warning: '#fab219',
    serious: '#ec835a',
    critical: '#d03b3b',
};

const hexToRgba = (hex, alpha) => {
    let m = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex);
    if (!m) return hex;
    return `rgba(${parseInt(m[1], 16)}, ${parseInt(m[2], 16)}, ${parseInt(m[3], 16)}, ${alpha})`;
};

const styles = {
    filterBar: {
        display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: 14,
        backgroundColor: palette.surface, borderRadius: 6, padding: '16px 18px',
        boxShadow: '0 10px 20px rgba(0,0,0,0.06)', marginBottom: 20, position: 'relative',
    },
    filterGroup: { marginBottom: 0, minWidth: 140 },
    filterLabel: { fontSize: 11, fontWeight: 600, color: palette.textMuted, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 4 },
    header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, flexWrap: 'wrap', gap: 10 },
    title: { fontSize: 16, fontWeight: 700, color: palette.textPrimary, margin: 0 },
    costInfoCard: {
        backgroundColor: palette.surface, borderRadius: 6, padding: '14px 16px', marginBottom: 16,
        boxShadow: '0 10px 20px rgba(0,0,0,0.06)', fontSize: 13, color: palette.textSecondary,
    },
    costInfoLine: { marginBottom: 6 },
    kpiRow: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 12, marginBottom: 20 },
    rateRow: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, marginBottom: 20 },
    tile: {
        backgroundColor: palette.surface, borderRadius: 6, padding: '14px 16px',
        boxShadow: '0 10px 20px rgba(0,0,0,0.06)',
    },
    tileLabel: { fontSize: 11, fontWeight: 600, color: palette.textMuted, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 6 },
    tileValue: { fontSize: 24, fontWeight: 600, color: palette.textPrimary, lineHeight: 1.1 },
    tileSub: { fontSize: 12, color: palette.textSecondary, marginTop: 4 },
    chartCard: {
        backgroundColor: palette.surface, borderRadius: 6, padding: '16px 18px 8px 18px',
        boxShadow: '0 10px 20px rgba(0,0,0,0.06)', marginBottom: 20,
    },
    sectionTitle: { fontSize: 13, fontWeight: 700, color: palette.textPrimary, marginBottom: 10 },
    summaryCard: {
        backgroundColor: palette.surface, borderRadius: 6, padding: '14px 18px',
        boxShadow: '0 10px 20px rgba(0,0,0,0.06)', marginBottom: 20,
        display: 'flex', flexWrap: 'wrap', gap: 24,
    },
    summaryItem: { minWidth: 140 },
    summaryLabel: { fontSize: 11, color: palette.textMuted, textTransform: 'uppercase', letterSpacing: 0.3 },
    summaryValue: { fontSize: 14, color: palette.textPrimary, marginTop: 2, wordBreak: 'break-word' },
    chip: {
        display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 10px',
        borderRadius: 16, backgroundColor: palette.border, fontSize: 13, color: palette.textPrimary,
    },
    chipClose: { border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 14, color: palette.textMuted, lineHeight: 1 },
    stackedBarOuter: { display: 'flex', height: 28, borderRadius: 6, overflow: 'hidden', backgroundColor: palette.border },
    legendRow: { display: 'flex', flexWrap: 'wrap', gap: 18, marginTop: 10 },
    legendItem: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: palette.textSecondary },
    swatch: { width: 10, height: 10, borderRadius: 2, flexShrink: 0 },
    emptyState: { textAlign: 'center', padding: '50px 20px', color: palette.textMuted, fontSize: 14 },
};

const METHODS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'];

function StatTile({ label, value, sub }) {
    return (
        <div style={styles.tile}>
            <div style={styles.tileLabel}>{label}</div>
            <div style={styles.tileValue}>{value}</div>
            {sub ? <div style={styles.tileSub}>{sub}</div> : null}
        </div>
    );
}

function RateTile({ label, avg, peak, unit, lang }) {
    let peakAt = peak && peak.at ? moment(peak.at).format('HH:mm:ss') : null;
    return (
        <div style={styles.tile}>
            <div style={styles.tileLabel}>{label}</div>
            <div style={styles.tileValue}>{avg !== null && avg !== undefined ? Number(avg).toFixed(2) : '0'} <span style={{ fontSize: 13, color: palette.textMuted }}>{unit}</span></div>
            <div style={styles.tileSub}>
                {peak && peak.requests
                    ? `${'Peak'.translate(lang)} ${formatNumber(peak.requests)} ${unit}${peakAt ? ` ${'at'.translate(lang)} ${peakAt}` : ''}`
                    : '-'}
            </div>
        </div>
    );
}

function AuthMethodBar({ byAuthMethod, lang }) {
    let rows = ['apiKey', 'basic', 'none'].map((method) => {
        let found = (byAuthMethod || []).find((r) => r.authMethod === method);
        return {
            method,
            color: method === 'apiKey' ? palette.catApiKey : method === 'basic' ? palette.catBasic : palette.catNone,
            requests: found ? found.requests : 0,
        };
    });
    let total = rows.reduce((sum, r) => sum + r.requests, 0);

    if (!total) {
        return <div style={{ color: palette.textMuted, fontSize: 13 }}>{'No requests in the selected period'.translate(lang)}</div>;
    }

    return (
        <div>
            <div style={styles.stackedBarOuter}>
                {rows.filter((r) => r.requests > 0).map((r, idx, arr) => (
                    <div
                        key={r.method}
                        style={{
                            flex: `${r.requests} 0 0`,
                            backgroundColor: r.color,
                            borderRight: idx < arr.length - 1 ? `2px solid ${palette.surface}` : 'none',
                        }}
                        title={`${authMethodLabel(r.method, lang)}: ${formatNumber(r.requests)} (${formatPercent(r.requests, total)})`}
                    />
                ))}
            </div>
            <div style={styles.legendRow}>
                {rows.map((r) => (
                    <div key={r.method} style={styles.legendItem}>
                        <span style={{ ...styles.swatch, backgroundColor: r.color }} />
                        {authMethodLabel(r.method, lang)}: {formatNumber(r.requests)} ({formatPercent(r.requests, total)})
                    </div>
                ))}
            </div>
        </div>
    );
}

function Timeseries({ timeseries, interval, lang }) {
    let fmt = interval === 'minute' ? 'HH:mm' : interval === 'day' ? 'MM-DD' : 'MM-DD HH:mm';
    let labels = (timeseries || []).map((p) => moment(p.at).format(fmt));

    let data = {
        labels,
        datasets: [
            {
                label: 'Requests'.translate(lang),
                data: (timeseries || []).map((p) => p.requests),
                borderColor: palette.textSecondary,
                backgroundColor: 'transparent',
                borderWidth: 2,
                pointRadius: 0,
                pointHoverRadius: 4,
                tension: 0.15,
            },
            {
                label: 'Errors'.translate(lang),
                data: (timeseries || []).map((p) => p.errors),
                borderColor: palette.critical,
                backgroundColor: hexToRgba(palette.critical, 0.1),
                fill: true,
                borderWidth: 2,
                pointRadius: 0,
                pointHoverRadius: 4,
                tension: 0.15,
            },
            {
                label: 'Auth failures'.translate(lang),
                data: (timeseries || []).map((p) => p.authFailures),
                borderColor: palette.warning,
                backgroundColor: 'transparent',
                borderWidth: 2,
                borderDash: [5, 3],
                pointRadius: 0,
                pointHoverRadius: 4,
                tension: 0.15,
            },
        ],
    };

    let options = {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: 'index', intersect: false },
        plugins: {
            legend: { position: 'top', align: 'start', labels: { usePointStyle: true, boxWidth: 8, color: palette.textSecondary, font: { size: 12 } } },
            tooltip: { mode: 'index', intersect: false },
        },
        scales: {
            x: { grid: { display: false }, ticks: { color: palette.textMuted, maxRotation: 0, autoSkip: true, font: { size: 11 } } },
            y: { beginAtZero: true, grid: { color: palette.border }, ticks: { color: palette.textMuted, precision: 0, font: { size: 11 } } },
        },
    };

    return (
        <div style={{ height: 280 }}>
            <Line data={data} options={options} />
        </div>
    );
}

/**
* Cluster load over time (VD-725 cost model): one series, so it is the
* sequential default - a single blue, light wash under the line. The two
* flat reference series (heavy threshold, full capacity) are drawn the same
* way rather than via an annotation plugin, since none is bundled here; they
* carry their own legend entries instead of a floating label so they read
* the same way the real series does.
*/
function LoadChart({ timeseries, interval, cost, lang }) {
    let fmt = interval === 'minute' ? 'HH:mm' : interval === 'day' ? 'MM-DD' : 'MM-DD HH:mm';
    let labels = (timeseries || []).map((p) => moment(p.at).format(fmt));
    let heavyAt = cost && cost.heavyPeakLoadPercent ? Number(cost.heavyPeakLoadPercent) : 25;

    let data = {
        labels,
        datasets: [
            {
                label: 'Load %'.translate(lang),
                data: (timeseries || []).map((p) => p.loadPercent),
                borderColor: palette.catApiKey,
                backgroundColor: hexToRgba(palette.catApiKey, 0.12),
                fill: true,
                borderWidth: 2,
                pointRadius: 0,
                pointHoverRadius: 4,
                tension: 0.15,
            },
            {
                label: `${'Heavy threshold'.translate(lang)} (${heavyAt}%)`,
                data: labels.map(() => heavyAt),
                borderColor: palette.warning,
                backgroundColor: 'transparent',
                borderWidth: 1.5,
                borderDash: [6, 4],
                pointRadius: 0,
                tension: 0,
            },
            {
                label: `${'Full capacity'.translate(lang)} (100%)`,
                data: labels.map(() => 100),
                borderColor: palette.critical,
                backgroundColor: 'transparent',
                borderWidth: 1.5,
                borderDash: [2, 3],
                pointRadius: 0,
                tension: 0,
            },
        ],
    };

    let options = {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: 'index', intersect: false },
        plugins: {
            legend: { position: 'top', align: 'start', labels: { usePointStyle: true, boxWidth: 8, color: palette.textSecondary, font: { size: 12 } } },
            tooltip: {
                mode: 'index',
                intersect: false,
                callbacks: {
                    label: (ctx) => `${ctx.dataset.label}: ${Number(ctx.parsed.y).toFixed(1)}%`,
                },
            },
        },
        scales: {
            x: { grid: { display: false }, ticks: { color: palette.textMuted, maxRotation: 0, autoSkip: true, font: { size: 11 } } },
            y: { beginAtZero: true, grid: { color: palette.border }, ticks: { color: palette.textMuted, callback: (v) => `${v}%`, font: { size: 11 } } },
        },
    };

    return (
        <div style={{ height: 280 }}>
            <Line data={data} options={options} />
        </div>
    );
}

/**
* Load (or points) per interval split by client (`clientSeries`, global view
* only). Identity, so each client takes its slot from the page-level color
* registry - the same color it has on the Overview tab - and everyone else
* folds into a neutral "Other".
*/
function ClientSeriesChart({ clientSeries, interval, metric, colorFor, onOpenClient, lang }) {
    let clients = (clientSeries && clientSeries.clients) || [];
    let points = (clientSeries && clientSeries.points) || [];
    let fmt = interval === 'minute' ? 'HH:mm' : interval === 'day' ? 'MM-DD' : 'MM-DD HH:mm';
    let labels = points.map((p) => moment(p.at).format(fmt));
    let colorOf = colorFor || ((id) => CLIENT_COLORS[Math.max(0, clients.findIndex((c) => c.clientId === id)) % CLIENT_COLORS.length]);

    let datasets = clients.map((c) => ({
        label: clientDisplayName(c),
        data: points.map((p) => (p.clients && p.clients[c.clientId] ? p.clients[c.clientId][metric] || 0 : 0)),
        backgroundColor: colorOf(c.clientId),
        borderColor: palette.surface,
        borderWidth: { top: 1 },
        borderSkipped: false,
        stack: 'clients',
    }));

    datasets.push({
        label: 'Other'.translate(lang),
        data: points.map((p) => (p.other ? p.other[metric] || 0 : 0)),
        backgroundColor: OTHER_COLOR,
        borderColor: palette.surface,
        borderWidth: { top: 1 },
        borderSkipped: false,
        stack: 'clients',
    });

    let isLoad = metric === 'loadPercent';

    let options = {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: 'index', intersect: false },
        plugins: {
            legend: { display: false },
            tooltip: {
                mode: 'index',
                intersect: false,
                itemSort: (a, b) => b.parsed.y - a.parsed.y,
                filter: (item) => item.parsed.y > 0,
                callbacks: {
                    label: (ctx) => `${ctx.dataset.label}: ${isLoad ? formatLoadPercent(ctx.parsed.y) : formatPoints(ctx.parsed.y)}`,
                },
            },
        },
        scales: {
            x: { stacked: true, grid: { display: false }, ticks: { color: palette.textMuted, maxRotation: 0, autoSkip: true, font: { size: 11 } } },
            y: {
                stacked: true, beginAtZero: true, grid: { color: palette.border },
                ticks: { color: palette.textMuted, font: { size: 11 }, ...(isLoad ? { callback: (v) => `${v}%` } : { precision: 0 }) },
            },
        },
    };

    return (
        <div>
            <div style={{ height: 300 }}>
                <Bar data={{ labels, datasets }} options={options} />
            </div>
            <div style={styles.legendRow}>
                {clients.map((c) => (
                    <button
                        key={c.clientId}
                        type="button"
                        style={{ ...styles.legendItem, background: 'transparent', border: 'none', padding: 0, cursor: onOpenClient ? 'pointer' : 'default' }}
                        onClick={() => onOpenClient && onOpenClient(c)}
                    >
                        <span style={{ ...styles.swatch, backgroundColor: colorOf(c.clientId) }} />
                        {clientDisplayName(c)}
                    </button>
                ))}
                <span style={styles.legendItem}>
                    <span style={{ ...styles.swatch, backgroundColor: OTHER_COLOR }} />
                    {'Other'.translate(lang)}
                </span>
            </div>
        </div>
    );
}

const minuteClientName = (c) => c.company || c.client || c.username || c.clientId || '-';

// filters a parent page can own instead of this component (see `scope`)
const SCOPE_KEYS = ['clientId', 'clientLabel', 'apiKeyId', 'apiKeyLabel', 'route', 'method', 'replica'];

const defaultFilters = {
    preset: '24h',
    customFrom: '',
    customTo: '',
    interval: '',
    clientId: '',
    clientLabel: '',
    route: '',
    method: '',
    apiKeyId: '',
    apiKeyLabel: '',
    replica: '',
};

/**
* @param {string} [clientId] - fix the component's scope to one client (used on the client detail tab)
* @param {object} [externalApiSummary] - that client's `externalApi` summary row from the clients list, shown as a header
* @param {object} [initialFilters] - seed values, read once on mount (used by the standalone page to restore from the URL)
* @param {function} [onFiltersChange] - called with the full filters object whenever they change, so a parent can mirror them into the URL
* @param {function} [onOpenClient] - called with a topClients row when the user clicks it; the standalone page uses this to navigate
* @param {function} [onOpenLogs] - called with a filters object when an Insights row links out to the logs view
* @param {object} [range] - {from, to} ISO strings owned by the parent page; hides this component's own period picker
* @param {object} [scope] - {clientId, clientLabel, apiKeyId, apiKeyLabel, route, method, replica} owned by the parent page; hides those filters here
* @param {function} [onScopeChange] - called with a patch of `scope` keys when a row click narrows one of them (e.g. an API key row)
* @param {boolean} [showInsights=true] - render the Insights panel under the tables
* @param {number} [clientSeries] - ask for a per-client breakdown of the top N clients (global view only) and chart it
* @param {function} [colorFor] - clientId -> color, shared with other charts on the page so a client keeps its color
* @param {function} [onReplicasLoaded] - called with the replica slots seen in an unfiltered-by-replica response, for a parent's replica dropdown
*/
function ExternalApiAnalytics({
    lang, clientId, externalApiSummary, initialFilters, onFiltersChange, onOpenClient, onOpenLogs,
    range: externalRange, scope, onScopeChange, showInsights = true, clientSeries, colorFor, onReplicasLoaded,
}) {
    let [filters, setFilters] = useState({ ...defaultFilters, ...(initialFilters || {}) });
    let [routeDraft, setRouteDraft] = useState(filters.route || '');
    let [data, setData] = useState(null);
    let [loading, setLoading] = useState(true);
    let [error, setError] = useState(null);
    let [activeTable, setActiveTable] = useState(null);
    let [chartView, setChartView] = useState('requests');
    let [showCostInfo, setShowCostInfo] = useState(false);
    let [seriesMetric, setSeriesMetric] = useState('loadPercent');
    // replica slots seen so far, so the dropdown keeps its options once one is picked
    let [replicaSlots, setReplicaSlots] = useState([]);
    let routeTimer = useRef(null);

    let updateFilters = useCallback((patch) => {
        setFilters((prev) => {
            let next = { ...prev, ...patch };
            if (onFiltersChange) onFiltersChange(next);
            return next;
        });
    }, [onFiltersChange]);

    // debounce the free-text route filter
    useEffect(() => {
        if (routeDraft === filters.route) return;
        if (routeTimer.current) clearTimeout(routeTimer.current);
        routeTimer.current = setTimeout(() => updateFilters({ route: routeDraft }), 300);
        return () => { if (routeTimer.current) clearTimeout(routeTimer.current); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [routeDraft]);

    // the effective filters: the parent's scope (when it owns one) wins
    let f = scope ? { ...filters, ...scope } : filters;

    // a row click that narrows client/key/route/method goes to whoever owns that filter
    let applyFilterPatch = (patch) => {
        if (!scope || !onScopeChange) {
            updateFilters(patch);
            return;
        }

        let scoped = {};
        let own = {};
        Object.keys(patch).forEach((key) => { (SCOPE_KEYS.indexOf(key) !== -1 ? scoped : own)[key] = patch[key]; });
        if (Object.keys(scoped).length) onScopeChange(scoped);
        if (Object.keys(own).length) updateFilters(own);
    };

    let ownRange = useMemo(() => {
        if (externalRange) return null;
        if (filters.preset !== 'custom') {
            return presetRange(filters.preset) || presetRange('24h');
        }

        let from = filters.customFrom ? moment(filters.customFrom).toISOString() : null;
        let to = filters.customTo ? moment(filters.customTo).toISOString() : null;

        return (from && to) ? { from, to } : null;
    }, [externalRange, filters.preset, filters.customFrom, filters.customTo]);

    let range = externalRange || ownRange;

    let rangeTooLong = useMemo(() => {
        if (!range) return false;
        return moment(range.to).diff(moment(range.from), 'days', true) > MAX_RANGE_DAYS;
    }, [range]);

    let requestKey = useMemo(() => JSON.stringify({
        from: range ? range.from : null,
        to: range ? range.to : null,
        interval: filters.interval,
        clientId: clientId || f.clientId,
        apiKeyId: f.apiKeyId,
        route: f.route,
        method: f.method,
        replica: f.replica,
        clientSeries,
    }), [range, filters.interval, f.clientId, f.apiKeyId, f.route, f.method, f.replica, clientId, clientSeries]);

    useEffect(() => {
        if (!range) {
            setLoading(false);
            setData(null);
            setError(null);
            return;
        }

        setLoading(true);
        setError(null);

        fetchExternalApiStats({
            from: range.from,
            to: range.to,
            interval: filters.interval || undefined,
            clientId: clientId || f.clientId || undefined,
            apiKeyId: f.apiKeyId || undefined,
            route: f.route || undefined,
            method: f.method || undefined,
            replica: f.replica,
            clientSeries: clientSeries || undefined,
        }).then(({ data: result, error: err }) => {
            setLoading(false);
            if (err) {
                setError(err);
                setData(null);
            } else {
                setData(result);

                let slots = ((result && result.byReplica) || []).map((r) => r.replica).filter(isKnownReplica);
                setReplicaSlots((prev) => Array.from(new Set([...prev, ...slots])).sort((a, b) => Number(a) - Number(b)));
                if (onReplicasLoaded && !f.replica) onReplicasLoaded(slots);
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [requestKey]);

    let showTopClients = data && data.topClients !== null && data.topClients !== undefined;

    let tabs = useMemo(() => {
        let list = [];
        list.push({ key: 'busiestMinutes', label: 'Busiest minutes'.translate(lang) });
        if (showTopClients) list.push({ key: 'topClients', label: 'Top clients'.translate(lang) });
        list.push({ key: 'apiKeys', label: 'API keys'.translate(lang) });
        list.push({ key: 'byPath', label: 'Routes'.translate(lang) });
        list.push({ key: 'ips', label: 'IP addresses'.translate(lang) });
        list.push({ key: 'userAgents', label: 'User agents'.translate(lang) });
        list.push({ key: 'byAuthMethod', label: 'Auth methods'.translate(lang) });
        list.push({ key: 'authErrors', label: 'Auth errors'.translate(lang) });
        list.push({ key: 'byStatus', label: 'Statuses'.translate(lang) });
        return list;
    }, [showTopClients, lang]);

    let currentTab = activeTable && tabs.find((t) => t.key === activeTable) ? activeTable : (tabs[0] ? tabs[0].key : null);

    let totals = (data && data.totals) || {};
    let rates = (data && data.rates) || {};
    let peaks = (data && data.peaks) || {};
    let cost = data && data.cost;
    let isEmpty = data && !totals.requests;

    return (
        <div>
            <div style={styles.filterBar}>
                {!externalRange ? (
                <FormGroup style={styles.filterGroup}>
                    <div style={styles.filterLabel}>{'Period'.translate(lang)}</div>
                    <Input
                        type="select"
                        style={{ height: 42 }}
                        value={filters.preset}
                        onChange={(e) => updateFilters({ preset: e.target.value })}
                    >
                        {PERIOD_PRESETS.map((p) => (
                            <option key={p.value} value={p.value}>{p.label.translate(lang)}</option>
                        ))}
                    </Input>
                </FormGroup>
                ) : null}

                {!externalRange && filters.preset === 'custom' ? (
                    <>
                        <FormGroup style={styles.filterGroup}>
                            <div style={styles.filterLabel}>{'From'.translate(lang)}</div>
                            <Input
                                type="datetime-local"
                                style={{ height: 42 }}
                                value={filters.customFrom}
                                onChange={(e) => updateFilters({ customFrom: e.target.value })}
                            />
                        </FormGroup>
                        <FormGroup style={styles.filterGroup}>
                            <div style={styles.filterLabel}>{'To'.translate(lang)}</div>
                            <Input
                                type="datetime-local"
                                style={{ height: 42 }}
                                value={filters.customTo}
                                onChange={(e) => updateFilters({ customTo: e.target.value })}
                            />
                        </FormGroup>
                    </>
                ) : null}

                <FormGroup style={styles.filterGroup}>
                    <div style={styles.filterLabel}>{'Interval'.translate(lang)}</div>
                    <Input
                        type="select"
                        style={{ height: 42 }}
                        value={filters.interval}
                        onChange={(e) => updateFilters({ interval: e.target.value })}
                    >
                        {INTERVAL_OPTIONS.map((o) => (
                            <option key={o.value} value={o.value}>{o.label.translate(lang)}</option>
                        ))}
                    </Input>
                    {!filters.interval && data && data.range && data.range.interval ? (
                        <div style={{ fontSize: 11, color: palette.textMuted, marginTop: 4 }}>
                            {'Backend used'.translate(lang)}: {data.range.interval}
                        </div>
                    ) : null}
                </FormGroup>

                {!scope ? (
                <>
                {!clientId ? (
                    <FormGroup style={styles.filterGroup}>
                        <div style={styles.filterLabel}>{'Client'.translate(lang)}</div>
                        <ClientAutocompleteInput
                            lang={lang}
                            value={filters.clientId}
                            label={filters.clientLabel}
                            onSelect={(item) => updateFilters({ clientId: item._id, clientLabel: item.username })}
                            onClear={() => updateFilters({ clientId: '', clientLabel: '' })}
                        />
                    </FormGroup>
                ) : null}

                <FormGroup style={styles.filterGroup}>
                    <div style={styles.filterLabel}>{'Route'.translate(lang)}</div>
                    <Input
                        type="text"
                        style={{ height: 42, minWidth: 160 }}
                        placeholder="/v2/orders"
                        value={routeDraft}
                        onChange={(e) => setRouteDraft(e.target.value)}
                    />
                </FormGroup>

                <FormGroup style={styles.filterGroup}>
                    <div style={styles.filterLabel}>{'Method'.translate(lang)}</div>
                    <Input
                        type="select"
                        style={{ height: 42 }}
                        value={filters.method}
                        onChange={(e) => updateFilters({ method: e.target.value })}
                    >
                        <option value="">{'All'.translate(lang)}</option>
                        {METHODS.map((m) => <option key={m} value={m}>{m}</option>)}
                    </Input>
                </FormGroup>

                {replicaSlots.length || filters.replica ? (
                    <FormGroup style={styles.filterGroup}>
                        <div style={styles.filterLabel}>{'Replica'.translate(lang)}</div>
                        <Input type="select" style={{ height: 42 }} value={filters.replica} onChange={(e) => updateFilters({ replica: e.target.value })}>
                            <option value="">{'All'.translate(lang)}</option>
                            {Array.from(new Set([...replicaSlots.map(String), ...(filters.replica ? [String(filters.replica)] : [])])).map((r) => (
                                <option key={r} value={r}>{r}</option>
                            ))}
                        </Input>
                    </FormGroup>
                ) : null}

                {filters.apiKeyId ? (
                    <FormGroup style={styles.filterGroup}>
                        <div style={styles.filterLabel}>{'API key'.translate(lang)}</div>
                        <div style={styles.chip}>
                            {filters.apiKeyLabel || filters.apiKeyId}
                            <button type="button" style={styles.chipClose} onClick={() => updateFilters({ apiKeyId: '', apiKeyLabel: '' })} aria-label={'Clear'.translate(lang)}>&times;</button>
                        </div>
                    </FormGroup>
                ) : null}

                <Button
                    color="secondary"
                    outline
                    style={{ height: 42 }}
                    onClick={() => {
                        setRouteDraft('');
                        updateFilters({ ...defaultFilters, preset: filters.preset, interval: filters.interval });
                    }}
                >
                    {'Reset filters'.translate(lang)}
                </Button>
                </>
                ) : null}
            </div>

            {rangeTooLong ? (
                <Alert color="warning">{`${'The range can be at most'.translate(lang)} ${MAX_RANGE_DAYS} ${'days'.translate(lang)}`}</Alert>
            ) : null}

            {!range && !rangeTooLong ? (
                <Alert color="info">{'Pick both a from and a to date for a custom period.'.translate(lang)}</Alert>
            ) : null}

            {error ? <Alert color="danger">{error}</Alert> : null}

            {externalApiSummary ? (
                <div style={styles.summaryCard}>
                    <div style={styles.summaryItem}>
                        <div style={styles.summaryLabel}>{'Last used'.translate(lang)}</div>
                        <div style={styles.summaryValue}>{externalApiSummary.lastUsedAt ? moment(externalApiSummary.lastUsedAt).format('YYYY-MM-DD HH:mm') : 'Never'.translate(lang)}</div>
                    </div>
                    <div style={styles.summaryItem}>
                        <div style={styles.summaryLabel}>{'Last IP'.translate(lang)}</div>
                        <div style={styles.summaryValue}>{externalApiSummary.lastUsedIp || '-'}</div>
                    </div>
                    <div style={styles.summaryItem}>
                        <div style={styles.summaryLabel}>{'Last user agent'.translate(lang)}</div>
                        <div style={{ ...styles.summaryValue, fontSize: 12 }}>{externalApiSummary.lastUserAgent || '-'}</div>
                    </div>
                    <div style={styles.summaryItem}>
                        <div style={styles.summaryLabel}>{'Auth method'.translate(lang)}</div>
                        <div style={styles.summaryValue}>{authMethodLabel(externalApiSummary.authMethod, lang)}</div>
                    </div>
                    <div style={styles.summaryItem}>
                        <div style={styles.summaryLabel}>{'Requests 7d'.translate(lang)}</div>
                        <div style={styles.summaryValue}>{formatNumber(externalApiSummary.requests7d)}</div>
                    </div>
                    <div style={styles.summaryItem}>
                        <div style={styles.summaryLabel}>{'Requests 30d'.translate(lang)}</div>
                        <div style={styles.summaryValue}>{formatNumber(externalApiSummary.requests30d)}</div>
                    </div>
                </div>
            ) : null}

            {loading ? (
                <div style={{ ...styles.emptyState, padding: '30px 20px' }}>{'Loading...'.translate(lang)}</div>
            ) : isEmpty ? (
                <div style={styles.emptyState}>{'No requests in the selected period'.translate(lang)}</div>
            ) : data ? (
                <>
                    <div style={styles.header}>
                        <h3 style={styles.title}>{'Cost'.translate(lang)}</h3>
                        <Button size="sm" color="secondary" outline onClick={() => setShowCostInfo((v) => !v)}>
                            {'How is this calculated'.translate(lang)}
                        </Button>
                    </div>

                    {showCostInfo ? (
                        <div style={styles.costInfoCard}>
                            {costModelLines(cost, lang).map((line, idx) => <div key={idx} style={styles.costInfoLine}>{line}</div>)}
                        </div>
                    ) : null}

                    <div style={styles.kpiRow}>
                        <StatTile label={'Points spent'.translate(lang)} value={formatPoints(totals.points)} />
                        <StatTile label={'Avg load'.translate(lang)} value={formatLoadPercent(totals.avgLoadPercent)} />
                        <StatTile
                            label={'Worst minute'.translate(lang)}
                            value={peaks.loadPerMinute ? <span className={loadBadgeClass(peaks.loadPerMinute.loadPercent, cost)} style={{ fontSize: 20 }}>{formatLoadPercent(peaks.loadPerMinute.loadPercent)}</span> : '-'}
                            sub={peaks.loadPerMinute && peaks.loadPerMinute.at ? (
                                <>
                                    {moment(peaks.loadPerMinute.at).format('YYYY-MM-DD HH:mm')}
                                    {(peaks.loadPerMinute.clients || []).length ? (
                                        <div style={{ marginTop: 4 }}>
                                            {peaks.loadPerMinute.clients.slice(0, 4).map((c, idx) => (
                                                <div key={idx}>{minuteClientName(c)}: {formatLoadPercent(c.loadPercent)}</div>
                                            ))}
                                        </div>
                                    ) : null}
                                </>
                            ) : null}
                        />
                        <StatTile label={'Avg points/request'.translate(lang)} value={totals.avgPointsPerRequest !== undefined && totals.avgPointsPerRequest !== null ? Number(totals.avgPointsPerRequest).toFixed(1) : '0'} />
                    </div>

                    <div style={styles.kpiRow}>
                        <StatTile label={'DB queries total'.translate(lang)} value={formatNumber(totals.dbQueries)} />
                        <StatTile label={'Avg DB queries/request'.translate(lang)} value={totals.avgDbQueries !== undefined && totals.avgDbQueries !== null ? Number(totals.avgDbQueries).toFixed(1) : '0'} />
                        <StatTile label={'% time in DB'.translate(lang)} value={formatLoadPercent(totals.dbTimePercent)} />
                    </div>

                    <div style={styles.kpiRow}>
                        <StatTile label={'Total requests'.translate(lang)} value={formatNumber(totals.requests)} />
                        <StatTile label={'Success %'.translate(lang)} value={formatPercent(totals.success, totals.requests)} />
                        <StatTile
                            label={'Errors (4xx / 5xx)'.translate(lang)}
                            value={`${formatNumber(totals.clientErrors)} / ${formatNumber(totals.serverErrors)}`}
                        />
                        <StatTile label={'Auth rejected'.translate(lang)} value={formatNumber(totals.authFailures)} />
                        <StatTile label={'Avg duration'.translate(lang)} value={formatMs(totals.avgDurationMs)} sub={`${'Max'.translate(lang)}: ${formatMs(totals.maxDurationMs)}`} />
                    </div>

                    <div style={styles.rateRow}>
                        <RateTile lang={lang} label={'Per second'.translate(lang)} avg={rates.avgPerSecond} peak={peaks.perSecond} unit="req/s" />
                        <RateTile lang={lang} label={'Per minute'.translate(lang)} avg={rates.avgPerMinute} peak={peaks.perMinute} unit="req/min" />
                        <RateTile lang={lang} label={'Per hour'.translate(lang)} avg={rates.avgPerHour} peak={peaks.perHour} unit="req/h" />
                        <RateTile lang={lang} label={'Per day'.translate(lang)} avg={rates.avgPerDay} peak={peaks.perDay} unit="req/day" />
                    </div>

                    <div style={styles.chartCard}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
                            <div style={styles.sectionTitle}>
                                {(chartView === 'load' ? 'Cluster load over time' : 'Requests over time').translate(lang)}
                            </div>
                            <div className="table-tabs" style={{ marginBottom: 0 }}>
                                <button className={chartView === 'requests' ? 'active' : ''} onClick={() => setChartView('requests')}>{'Requests'.translate(lang)}</button>
                                <button className={chartView === 'load' ? 'active' : ''} onClick={() => setChartView('load')}>{'Load %'.translate(lang)}</button>
                            </div>
                        </div>
                        {chartView === 'load' ? (
                            <LoadChart timeseries={data.timeseries} interval={data.range && data.range.interval} cost={cost} lang={lang} />
                        ) : (
                            <Timeseries timeseries={data.timeseries} interval={data.range && data.range.interval} lang={lang} />
                        )}
                        {data.range && data.range.interval && data.range.interval !== 'minute' ? (
                            <div style={{ fontSize: 12, color: palette.textMuted, margin: '6px 0 8px 0' }}>
                                {`${'The chart shows the average per'.translate(lang)} ${(data.range.interval === 'day' ? 'day' : 'hour').translate(lang)}, ${'not the peak. The worst minutes are in the "Busiest minutes" table below.'.translate(lang)}`}
                            </div>
                        ) : null}
                    </div>

                    {data.clientSeries && (data.clientSeries.clients || []).length ? (
                        <div style={styles.chartCard}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
                                <div style={styles.sectionTitle}>{'Load by client'.translate(lang)}</div>
                                <div className="table-tabs" style={{ marginBottom: 0 }}>
                                    <button className={seriesMetric === 'loadPercent' ? 'active' : ''} onClick={() => setSeriesMetric('loadPercent')}>{'Load %'.translate(lang)}</button>
                                    <button className={seriesMetric === 'points' ? 'active' : ''} onClick={() => setSeriesMetric('points')}>{'Points'.translate(lang)}</button>
                                </div>
                            </div>
                            <ClientSeriesChart
                                clientSeries={data.clientSeries}
                                interval={data.range && data.range.interval}
                                metric={seriesMetric}
                                colorFor={colorFor}
                                onOpenClient={onOpenClient}
                                lang={lang}
                            />
                            <div style={{ height: 8 }} />
                        </div>
                    ) : null}

                    <ExternalApiReplicas
                        byReplica={data.byReplica}
                        lang={lang}
                        onOpenReplica={(replica) => (onOpenLogs
                            ? onOpenLogs({ replica, ...(range ? { from: range.from, to: range.to } : {}) })
                            : applyFilterPatch({ replica }))}
                    />

                    <div className="table-tabs">
                        {tabs.map((t) => (
                            <button key={t.key} className={currentTab === t.key ? 'active' : ''} onClick={() => setActiveTable(t.key)}>
                                {t.label}
                            </button>
                        ))}
                    </div>

                    <div style={{ marginTop: 10 }}>
                        {currentTab === 'busiestMinutes' ? (
                            <Table responsive hover>
                                <thead>
                                    <tr>
                                        <th>{'Minute'.translate(lang)}</th>
                                        <th>{'Load'.translate(lang)}</th>
                                        <th>{'Points'.translate(lang)}</th>
                                        <th>{'Requests'.translate(lang)}</th>
                                        <th>{'Who took how much'.translate(lang)}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(data.busiestMinutes || []).length === 0 ? (
                                        <tr><td colSpan={5} style={{ color: palette.textMuted }}>{'No requests in the selected period'.translate(lang)}</td></tr>
                                    ) : (data.busiestMinutes || []).slice(0, 10).map((row, idx) => (
                                        <tr
                                            key={idx}
                                            style={{ cursor: 'pointer' }}
                                            onClick={() => onOpenLogs && onOpenLogs({ from: row.at, to: moment(row.at).add(60, 'seconds').toISOString() })}
                                        >
                                            <td>{row.at ? moment(row.at).format('YYYY-MM-DD HH:mm') : '-'}</td>
                                            <td>
                                                <span className={loadBadgeClass(row.loadPercent, cost)}>{formatLoadPercent(row.loadPercent)}</span>
                                                <div className="load-bar-track" style={{ marginTop: 4, maxWidth: 100 }}>
                                                    <div className="load-bar-fill" style={{ width: `${Math.max(0, Math.min(100, row.loadPercent || 0))}%`, backgroundColor: loadBarColor(row.loadPercent, cost) }} />
                                                </div>
                                            </td>
                                            <td>{formatPoints(row.points)}</td>
                                            <td>{formatNumber(row.requests)}</td>
                                            <td>
                                                {(row.clients || []).slice(0, 6).map((c, cIdx) => (
                                                    <span key={cIdx} style={{ ...styles.chip }}>
                                                        {minuteClientName(c)}: {c.loadPercent !== undefined ? formatLoadPercent(c.loadPercent) : formatPoints(c.points)}
                                                    </span>
                                                ))}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        ) : null}

                        {currentTab === 'topClients' ? (
                            <Table responsive hover>
                                <thead>
                                    <tr>
                                        <th>{'Client'.translate(lang)}</th>
                                        <th>{'Company'.translate(lang)}</th>
                                        <th>{'Customer nr.'.translate(lang)}</th>
                                        <th>{'Points'.translate(lang)}</th>
                                        <th>{'Share'.translate(lang)}</th>
                                        <th>{'Avg points/req'.translate(lang)}</th>
                                        <th>{'Requests'.translate(lang)}</th>
                                        <th>{'DB queries'.translate(lang)}</th>
                                        <th>{'Errors'.translate(lang)}</th>
                                        <th>{'First'.translate(lang)}</th>
                                        <th>{'Last'.translate(lang)}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(data.topClients || []).length === 0 ? (
                                        <tr><td colSpan={11} style={{ color: palette.textMuted }}>{'No requests in the selected period'.translate(lang)}</td></tr>
                                    ) : (data.topClients || []).map((row) => (
                                        <tr
                                            key={row.clientId}
                                            style={onOpenClient ? { cursor: 'pointer' } : undefined}
                                            onClick={() => onOpenClient && onOpenClient(row)}
                                        >
                                            <td>{row.username}</td>
                                            <td>{row.company || '-'}</td>
                                            <td>{row.customerNumber || '-'}</td>
                                            <td>{formatPoints(row.points)}</td>
                                            <td>{formatLoadPercent(row.sharePercent)}</td>
                                            <td>{row.avgPointsPerRequest !== undefined && row.avgPointsPerRequest !== null ? Number(row.avgPointsPerRequest).toFixed(1) : '-'}</td>
                                            <td>{formatNumber(row.requests)}</td>
                                            <td>{formatNumber(row.dbQueries)}</td>
                                            <td>{formatNumber(row.errors)}</td>
                                            <td>{row.firstAt ? moment(row.firstAt).format('YYYY-MM-DD HH:mm') : '-'}</td>
                                            <td>{row.lastAt ? moment(row.lastAt).format('YYYY-MM-DD HH:mm') : '-'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        ) : null}

                        {currentTab === 'apiKeys' ? (
                            <Table responsive hover>
                                <thead>
                                    <tr>
                                        <th>{'Name'.translate(lang)}</th>
                                        <th>{'Prefix'.translate(lang)}</th>
                                        <th>{'Requests'.translate(lang)}</th>
                                        <th>{'Errors'.translate(lang)}</th>
                                        <th>{'Last used'.translate(lang)}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(data.apiKeys || []).length === 0 ? (
                                        <tr><td colSpan={5} style={{ color: palette.textMuted }}>{'No requests in the selected period'.translate(lang)}</td></tr>
                                    ) : (data.apiKeys || []).map((row) => (
                                        <tr
                                            key={row.apiKeyId}
                                            style={{ cursor: 'pointer' }}
                                            onClick={() => applyFilterPatch({ apiKeyId: row.apiKeyId, apiKeyLabel: row.name })}
                                        >
                                            <td>{row.name}</td>
                                            <td><code>{row.prefix}</code></td>
                                            <td>{formatNumber(row.requests)}</td>
                                            <td>{formatNumber(row.errors)}</td>
                                            <td>{row.lastAt ? moment(row.lastAt).format('YYYY-MM-DD HH:mm') : '-'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        ) : null}

                        {currentTab === 'byPath' ? (
                            <Table responsive hover>
                                <thead>
                                    <tr>
                                        <th>{'Method'.translate(lang)}</th>
                                        <th>{'Route'.translate(lang)}</th>
                                        <th>{'Points'.translate(lang)}</th>
                                        <th>{'Share'.translate(lang)}</th>
                                        <th>{'Avg points/req'.translate(lang)}</th>
                                        <th>{'Requests'.translate(lang)}</th>
                                        <th>{'Errors'.translate(lang)}</th>
                                        <th>{'Error %'.translate(lang)}</th>
                                        <th>{'Avg duration'.translate(lang)}</th>
                                        <th>{'Avg DB queries'.translate(lang)}</th>
                                        <th>{'Max DB queries'.translate(lang)}</th>
                                        <th>{'% in DB'.translate(lang)}</th>
                                        <th>{'Last used'.translate(lang)}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(data.byPath || []).length === 0 ? (
                                        <tr><td colSpan={13} style={{ color: palette.textMuted }}>{'No requests in the selected period'.translate(lang)}</td></tr>
                                    ) : (data.byPath || []).map((row, idx) => (
                                        <tr
                                            key={idx}
                                            style={{ cursor: 'pointer' }}
                                            onClick={() => { setRouteDraft(row.route); applyFilterPatch({ route: row.route, method: row.method }); }}
                                        >
                                            <td>{row.method}</td>
                                            <td><code>{row.route}</code></td>
                                            <td>{formatPoints(row.points)}</td>
                                            <td>{formatLoadPercent(row.sharePercent)}</td>
                                            <td>{row.avgPointsPerRequest !== undefined && row.avgPointsPerRequest !== null ? Number(row.avgPointsPerRequest).toFixed(1) : '-'}</td>
                                            <td>{formatNumber(row.requests)}</td>
                                            <td>{formatNumber(row.errors)}</td>
                                            <td>{formatPercent(row.errors, row.requests)}</td>
                                            <td>{formatMs(row.avgDurationMs)}</td>
                                            <td>{row.avgDbQueries !== undefined && row.avgDbQueries !== null ? Number(row.avgDbQueries).toFixed(1) : '-'}</td>
                                            <td>{formatNumber(row.maxDbQueries)}</td>
                                            <td>{formatLoadPercent(row.dbTimePercent)}</td>
                                            <td>{row.lastAt ? moment(row.lastAt).format('YYYY-MM-DD HH:mm') : '-'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        ) : null}

                        {currentTab === 'ips' ? (
                            <Table responsive hover>
                                <thead>
                                    <tr>
                                        <th>{'IP'.translate(lang)}</th>
                                        <th>{'Clients'.translate(lang)}</th>
                                        <th>{'Requests'.translate(lang)}</th>
                                        <th>{'Errors'.translate(lang)}</th>
                                        <th>{'First'.translate(lang)}</th>
                                        <th>{'Last'.translate(lang)}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(data.ips || []).length === 0 ? (
                                        <tr><td colSpan={6} style={{ color: palette.textMuted }}>{'No requests in the selected period'.translate(lang)}</td></tr>
                                    ) : (data.ips || []).map((row, idx) => (
                                        <tr key={idx}>
                                            <td><code>{row.ip}</code></td>
                                            <td>{(row.clients || []).join(', ') || '-'}</td>
                                            <td>{formatNumber(row.requests)}</td>
                                            <td>{formatNumber(row.errors)}</td>
                                            <td>{row.firstAt ? moment(row.firstAt).format('YYYY-MM-DD HH:mm') : '-'}</td>
                                            <td>{row.lastAt ? moment(row.lastAt).format('YYYY-MM-DD HH:mm') : '-'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        ) : null}

                        {currentTab === 'userAgents' ? (
                            <Table responsive hover>
                                <thead>
                                    <tr>
                                        <th>{'User agent'.translate(lang)}</th>
                                        <th>{'Requests'.translate(lang)}</th>
                                        <th>{'Errors'.translate(lang)}</th>
                                        <th>{'First'.translate(lang)}</th>
                                        <th>{'Last'.translate(lang)}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(data.userAgents || []).length === 0 ? (
                                        <tr><td colSpan={5} style={{ color: palette.textMuted }}>{'No requests in the selected period'.translate(lang)}</td></tr>
                                    ) : (data.userAgents || []).map((row, idx) => (
                                        <tr key={idx}>
                                            <td style={{ maxWidth: 420, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={row.userAgent}>{row.userAgent || '-'}</td>
                                            <td>{formatNumber(row.requests)}</td>
                                            <td>{formatNumber(row.errors)}</td>
                                            <td>{row.firstAt ? moment(row.firstAt).format('YYYY-MM-DD HH:mm') : '-'}</td>
                                            <td>{row.lastAt ? moment(row.lastAt).format('YYYY-MM-DD HH:mm') : '-'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        ) : null}

                        {currentTab === 'byAuthMethod' ? (
                            <div style={{ backgroundColor: palette.surface, borderRadius: 6, padding: '18px 20px', boxShadow: '0 10px 20px rgba(0,0,0,0.06)' }}>
                                <AuthMethodBar byAuthMethod={data.byAuthMethod} lang={lang} />
                            </div>
                        ) : null}

                        {currentTab === 'authErrors' ? (
                            <Table responsive hover>
                                <thead>
                                    <tr>
                                        <th>{'Reason'.translate(lang)}</th>
                                        <th>{'Requests'.translate(lang)}</th>
                                        <th>{'Errors'.translate(lang)}</th>
                                        <th>{'First'.translate(lang)}</th>
                                        <th>{'Last'.translate(lang)}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(data.authErrors || []).length === 0 ? (
                                        <tr><td colSpan={5} style={{ color: palette.textMuted }}>{'No requests in the selected period'.translate(lang)}</td></tr>
                                    ) : (data.authErrors || []).map((row, idx) => (
                                        <tr key={idx}>
                                            <td>{authErrorLabel(row.code)}</td>
                                            <td>{formatNumber(row.requests)}</td>
                                            <td>{formatNumber(row.errors)}</td>
                                            <td>{row.firstAt ? moment(row.firstAt).format('YYYY-MM-DD HH:mm') : '-'}</td>
                                            <td>{row.lastAt ? moment(row.lastAt).format('YYYY-MM-DD HH:mm') : '-'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        ) : null}

                        {currentTab === 'byStatus' ? (
                            <Table responsive hover>
                                <thead>
                                    <tr>
                                        <th>{'Status'.translate(lang)}</th>
                                        <th>{'Requests'.translate(lang)}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(data.byStatus || []).length === 0 ? (
                                        <tr><td colSpan={2} style={{ color: palette.textMuted }}>{'No requests in the selected period'.translate(lang)}</td></tr>
                                    ) : (data.byStatus || []).map((row, idx) => (
                                        <tr key={idx}>
                                            <td>{row.status}</td>
                                            <td>{formatNumber(row.requests)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        ) : null}
                    </div>

                    {showInsights ? (
                    <div style={{ marginTop: 28 }}>
                        {f.replica ? (
                            <div style={{ fontSize: 12, color: palette.textMuted, marginBottom: 8 }}>
                                {'The analysis below covers all replicas, it has no replica filter.'.translate(lang)}
                            </div>
                        ) : null}
                        <ExternalApiInsights
                            lang={lang}
                            clientId={clientId || f.clientId || undefined}
                            apiKeyId={f.apiKeyId || undefined}
                            route={f.route || undefined}
                            method={f.method || undefined}
                            from={range ? range.from : null}
                            to={range ? range.to : null}
                            totalsRequests={totals.requests}
                            onOpenLogs={onOpenLogs}
                            onOpenClient={onOpenClient}
                        />
                    </div>
                    ) : null}
                </>
            ) : null}
        </div>
    );
}

export default ExternalApiAnalytics;
